import { format } from 'date-fns';
import { Box } from '@mui/material';
import uk from 'date-fns/locale/uk';
import { DatePickerHeaderProps } from './DatePickerHeader.types';

/**
 * DatePickerHeader - Renders header with view toggle buttons and navigation controls
 *
 * Displays:
 * - View toggle buttons (Day, Month, Year, and optionally Time)
 * - Navigation arrows (previous/next)
 * - Current period title (month/year, year, or decade)
 *
 * @component
 * @param {DatePickerHeaderProps} props - Component props
 * @returns {JSX.Element} Header with controls and navigation
 *
 * @example
 * <DatePickerHeader
 *   view="day"
 *   displayDate={new Date()}
 *   onViewChange={setView}
 *   onNavigate={handleNav}
 *   showTimeButton={true}
 * />
 */
export const DatePickerHeader = ({
  view,
  displayDate,
  onViewChange,
  onNavigate,
  showTimeButton = false
}: DatePickerHeaderProps) => {
  /**
   * Generates the title text based on current view
   * - Day view: "Month Year" (e.g., "January 2024")
   * - Month view: "Year" (e.g., "2024")
   * - Year view: "Decade" (e.g., "2020")
   *
   * @returns {string} Formatted title for current view
   */
  const getTitle = () => {
    if (view === 'day') return format(displayDate, 'MMMM yyyy', { locale: uk });
    if (view === 'month') return displayDate.getFullYear().toString();
    if (view === 'time') return displayDate.getFullYear().toString();
    return Math.floor(displayDate.getFullYear() / 10) * 10;
  };

  return (
    <>
      <Box className="custom-date-picker-buttons">
        <button className={`date-view-btn ${view === 'day' ? 'active' : ''}`} onClick={() => onViewChange('day')}>
          День
        </button>
        <button className={`date-view-btn ${view === 'month' ? 'active' : ''}`} onClick={() => onViewChange('month')}>
          Місяць
        </button>
        <button className={`date-view-btn ${view === 'year' ? 'active' : ''}`} onClick={() => onViewChange('year')}>
          Рік
        </button>
        {showTimeButton && (
          <button className={`date-view-btn ${view === 'time' ? 'active' : ''}`} onClick={() => onViewChange('time')}>
            Час
          </button>
        )}
      </Box>

      {view !== 'time' && (
        <Box className="custom-date-picker-header">
          <button onClick={() => onNavigate('prev')} className="custom-date-picker-nav">
            ←
          </button>
          <span className="custom-date-picker-title">{getTitle()}</span>
          <button onClick={() => onNavigate('next')} className="custom-date-picker-nav">
            →
          </button>
        </Box>
      )}
    </>
  );
};
